/**
 * Reconnection manager with exponential backoff and jitter
 */

import { ReconnectionConfig } from '../types/connectionTypes';

const RECONNECTION_TIMEOUT = 30 * 1000;

export const DEFAULT_RECONNECTION_CONFIG: ReconnectionConfig = {
  initialDelay: 1000,       // 1 second
  maxDelay: 30000,          // 30 seconds
  maxRetries: 10,
  backoffMultiplier: 2,
  jitterRange: 0.3,          // ±30% randomization
  maxTotalTime: RECONNECTION_TIMEOUT
};

export class ReconnectionManager {
  private config: ReconnectionConfig;
  private attemptCount: number = 0;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private reconnectionStartTime: number | null = null;
  private pausedAt: number | null = null;

  constructor(config: Partial<ReconnectionConfig> = {}) {
    this.config = { ...DEFAULT_RECONNECTION_CONFIG, ...config };
  }

  private elapsedSinceStart(): number {
    if (this.reconnectionStartTime === null) return 0;
    const now = this.pausedAt !== null ? this.pausedAt : Date.now();
    return now - this.reconnectionStartTime;
  }

  /**
   * Calculate exponential backoff delay with jitter
   * Formula: min(initialDelay * (multiplier ^ attempt), maxDelay) * (1 + random(-jitter, +jitter))
   */
  calculateDelay(attempt: number): number {
    const { initialDelay, maxDelay, backoffMultiplier, jitterRange } = this.config;

    // Exponential backoff
    const exponentialDelay = initialDelay * Math.pow(backoffMultiplier, attempt - 1);

    // Cap at max delay
    const cappedDelay = Math.min(exponentialDelay, maxDelay);

    // Add jitter (randomization to prevent thundering herd)
    const jitter = 1 + (Math.random() * 2 - 1) * jitterRange; // Range: [1-jitterRange, 1+jitterRange]

    return Math.floor(cappedDelay * jitter);
  }

  /**
   * Start reconnection attempt
   * @returns delay in milliseconds before next attempt, or null if max retries or time limit reached
   */
  startReconnection(): number | null {
    if (this.reconnectionStartTime === null) {
      this.reconnectionStartTime = Date.now();
      console.log(`Reconnection started at ${new Date(this.reconnectionStartTime).toISOString()}`);
    }

    const elapsedTime = this.elapsedSinceStart();
    if (elapsedTime >= this.config.maxTotalTime) {
      console.warn(`Reconnection timeout reached (${this.config.maxTotalTime / 1000}s elapsed)`);
      return null;
    }

    this.attemptCount++;

    if (this.attemptCount > this.config.maxRetries) {
      console.warn(`Max reconnection attempts (${this.config.maxRetries}) reached`);
      return null;
    }

    const delay = this.calculateDelay(this.attemptCount);
    const remainingTime = this.config.maxTotalTime - elapsedTime;
    console.log(`Reconnection attempt ${this.attemptCount}/${this.config.maxRetries} scheduled in ${delay}ms (${Math.round(remainingTime / 1000)}s remaining)`);

    return delay;
  }

  /**
   * Schedule reconnection callback
   */
  scheduleReconnect(callback: () => void): void {
    const delay = this.startReconnection();

    if (delay === null) {
      // Max retries reached
      return;
    }

    // Clear any existing timer
    this.clearTimer();

    // Schedule reconnection
    this.reconnectTimer = setTimeout(() => {
      callback();
    }, delay);
  }

  pause(): void {
    if (this.pausedAt === null) {
      this.pausedAt = Date.now();
    }
    this.clearTimer();
  }

  resume(): void {
    if (this.pausedAt !== null) {
      if (this.reconnectionStartTime !== null) {
        this.reconnectionStartTime += Date.now() - this.pausedAt;
      }
      this.pausedAt = null;
    }
  }

  /**
   * Reset attempt counter (call on successful reconnection)
   */
  reset(): void {
    this.attemptCount = 0;
    this.reconnectionStartTime = null;
    this.pausedAt = null;
    this.clearTimer();
  }

  /**
   * Clear reconnection timer
   */
  clearTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Get current attempt count
   */
  getAttemptCount(): number {
    return this.attemptCount;
  }

  /**
   * Get max retries
   */
  getMaxRetries(): number {
    return this.config.maxRetries;
  }

  /**
   * Check if max retries reached
   */
  isMaxRetriesReached(): boolean {
    return this.attemptCount >= this.config.maxRetries;
  }

  isTimeoutReached(): boolean {
    if (this.reconnectionStartTime === null) return false;
    return this.elapsedSinceStart() >= this.config.maxTotalTime;
  }

  shouldStopReconnecting(): boolean {
    return this.isMaxRetriesReached() || this.isTimeoutReached();
  }

  /**
   * Check if currently attempting reconnection
   */
  isReconnecting(): boolean {
    return this.reconnectTimer !== null;
  }
}
